import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOkapiKy, useStripes } from '@folio/stripes/core';

/**
 * Provides mutations for scheduled reports:
 * create, update, delete, pause, resume, runNow
 *
 * Invalidates both query keys on success:
 * - ['scheduled-reports-list'] — the paginated list in ScheduledReportsRoute
 */
export default function useScheduledReportsMutations() {
  const okapiKy    = useOkapiKy();
  const stripes    = useStripes();
  const queryClient = useQueryClient();
  const userId     = stripes.user?.user?.id;

  const invalidate = () => {
    queryClient.invalidateQueries(['scheduled-reports-list']);
  };

  const createSchedule = useMutation({
    mutationFn: async (scheduleData) => {
      const payload = { ...scheduleData, userId };
      return await okapiKy.post('scheduled-reports', { json: payload }).json();
    },
    onSuccess: invalidate,
  });

  const updateSchedule = useMutation({
    mutationFn: async ({ scheduleId, updates }) => {
      await okapiKy.put(`scheduled-reports/${scheduleId}`, { json: updates });
    },
    onSuccess: invalidate,
  });

  const deleteSchedule = useMutation({
    mutationFn: async (scheduleId) => {
      return await okapiKy.delete(`scheduled-reports/${scheduleId}`);
    },
    onSuccess: invalidate,
  });

  const pauseSchedule = useMutation({
    mutationFn: async (scheduleId) => {
      return await okapiKy.post(`scheduled-reports/${scheduleId}/pause`);
    },
    onSuccess: invalidate,
  });

  const resumeSchedule = useMutation({
    mutationFn: async (scheduleId) => {
      return await okapiKy.post(`scheduled-reports/${scheduleId}/resume`);
    },
    onSuccess: invalidate,
  });

  const runNow = useMutation({
    mutationFn: async (scheduleId) => {
      return await okapiKy.post(`scheduled-reports/${scheduleId}/run-now`);
    },
  });

  return {
    createSchedule,
    updateSchedule,
    deleteSchedule,
    pauseSchedule,
    resumeSchedule,
    runNow,
  };
}
